import { type ReactNode, useCallback, useContext, useMemo } from 'react'

import { type AttributeInput } from '@data/shopify/storefront/types'
import { AnalyticsEventName, getProductEventPayload } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { CartContext } from '@lib/cart/context'
import { useAddItemsToCart } from '@lib/cart/hooks'
import { getShopifyCart } from '@lib/cart/request'
import { type CartVariantLineItem } from '@lib/cart/types'
import { getIsValidProductQuantity } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import { ShopContext } from '@lib/shop-context'
import { shopifyNewProductDelay } from '@lib/shopify/product'
import { StringsContext } from '@lib/strings-context'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { useFrameBuilderCartAttributes } from '@frame-builder/lib/cart'
import { useCreateFrameBuilderProduct } from '@frame-builder/lib/product-create'

import Button from '@frame-builder/components/utility/button'

interface AddToCartButtonProps {
  quantity: string
  getProductImageDataUrl: () => string | undefined
  children: ReactNode
}

const AddToCartButton = ({
  quantity,
  getProductImageDataUrl,
  children,
}: AddToCartButtonProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const {
    cart,
    cartAddingProductIds,
    isCreatingProduct,
    saveCart,
    setIsCreatingProduct,
    setIsCartUpdating,
    setCartAddingProductIds,
  } = useContext(CartContext)
  const { frameBuilderType } = useContext(FrameBuilderSettingsContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { frameCompletionPercentage, stepPreviewAnimation } = useContext(
    FrameBuilderValidationContext,
  )
  const { locale } = useContext(LanguageContext)
  const { currencyCode } = useContext(ShopContext)
  const strings = useContext(StringsContext)

  const isValidFrame = useMemo(() => {
    const isValidProductQuantity = getIsValidProductQuantity(quantity)

    return frameCompletionPercentage === 100 && isValidProductQuantity
  }, [frameCompletionPercentage, quantity])

  const frameBuilderCartAttributes = useFrameBuilderCartAttributes()
  const addItemsToCart = useAddItemsToCart()

  const createFrameBuilderProduct = useCreateFrameBuilderProduct(
    frameBuilderType,
    getProductImageDataUrl,
    setIsCreatingProduct,
  )

  const addToCart = useCallback(
    async (variantId: number, lineItemAttributes: AttributeInput[]) => {
      try {
        // Add frame builder product to cart
        const variantLineItem: CartVariantLineItem = {
          id: variantId,
          quantity: Number(quantity),
          attributes: lineItemAttributes,
        }

        setCartAddingProductIds([variantLineItem.id])
        setIsCartUpdating(true)

        await addItemsToCart([variantLineItem])

        setCartAddingProductIds([])
        setIsCartUpdating(false)
      } catch (error) {
        console.error(error)

        setCartAddingProductIds([])
        setIsCartUpdating(false)

        throw new Error('Product adding to cart failed')
      }
    },
    [addItemsToCart, quantity, setCartAddingProductIds, setIsCartUpdating],
  )

  const handleAddToCartClick = useCallback(async () => {
    // Validate frame builder
    if (!isValidFrame) {
      stepPreviewAnimation.startBlinking()
      return
    }

    try {
      const createProductResponse = await createFrameBuilderProduct()
      await addToCart(createProductResponse.variantId, [
        ...frameBuilderCartAttributes,
      ])

      // Read cart again after a timeout to get the image of the new product
      setTimeout(async () => {
        const newCart = await getShopifyCart(locale, cart.id)
        saveCart(newCart)
      }, shopifyNewProductDelay)

      // Trigger add to cart anaytics event
      const eventPayload = getProductEventPayload(
        createProductResponse.productTitle,
        [
          {
            variantId: createProductResponse.variantId,
            quantity: Number(quantity),
            price: createProductResponse.variantPrice,
            comparePrice: createProductResponse.variantComparePrice ?? 0,
            options: [],
          },
        ],
        currencyCode,
      )
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart, eventPayload)
    } catch (error) {
      console.error(error)
      alert(frameBuilderStrings.addProductError)

      return
    }
  }, [
    addToCart,
    cart.id,
    createFrameBuilderProduct,
    currencyCode,
    frameBuilderCartAttributes,
    frameBuilderStrings,
    isValidFrame,
    locale,
    quantity,
    saveCart,
    stepPreviewAnimation,
    triggerAnalyticsEvent,
  ])

  const isCartLoaded = !!cart.id
  const isAddingItems = cartAddingProductIds.length > 0

  return (
    <Button
      size="large"
      isVisuallyDisabled={!isValidFrame}
      disabled={!isCartLoaded || isCreatingProduct || isAddingItems}
      onClick={handleAddToCartClick}
    >
      {!isCartLoaded && <>{strings.buttonUnavailable}</>}
      {isCartLoaded && isCreatingProduct && (
        <>{strings.buttonCreatingProduct}</>
      )}
      {isCartLoaded && !isCreatingProduct && isAddingItems && (
        <>{strings.buttonAdding}</>
      )}
      {isCartLoaded && !isCreatingProduct && !isAddingItems && <>{children}</>}
    </Button>
  )
}

export default AddToCartButton
